/**
 * Horarios › Patrones (008 · T087 · US5; contracts/ui.md §1.4, spec US5).
 *
 * - Lista de patrones (Nombre, Tipo, Ciclo, Origen, Estado) con filtro de estado en borrador,
 *   "Buscar" y "Actualizar" (QA-2).
 * - "Nuevo patrón", "Editar" y "Archivar" con `schedules.configure`; "Aplicar a trabajadores" con
 *   `schedules.write` (el Encargado de sede aplica, pero no crea patrones). Un patrón de integración
 *   se ve con candado y sin acciones de edición; uno archivado no se aplica (sus aplicaciones
 *   activas siguen hasta su fin).
 * - Debajo, las aplicaciones (`applications-section.tsx`) con "Terminar".
 * - Empresa suspendida ⇒ acciones deshabilitadas (solo lectura).
 */
import { Lock, Plus, Repeat } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  CatalogStatusFilterSchema,
  type CatalogStatusFilter,
  type SchedulePattern,
} from '@fermin/contracts';

import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Card, CardDescription, CardTitle } from '../../../components/ui/card';
import { Dialog, DialogContent } from '../../../components/ui/dialog';
import { EmptyState } from '../../../components/ui/empty-state';
import { Field } from '../../../components/ui/field';
import { FilterActions, FilterForm } from '../../../components/ui/filter-actions';
import { RefreshButton } from '../../../components/ui/refresh-button';
import { Select, type SelectOption } from '../../../components/ui/select';
import { SkeletonLines } from '../../../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '../../../components/ui/table';
import { useToast } from '../../../components/ui/toast';
import { t } from '../../../i18n';
import { useAppliedFilters } from '../../../lib/use-applied-filters';
import { hasPermission, useMe } from '../../shell/use-me';
import { readOnlyProps, useReadOnly } from '../../shell/use-read-only';
import { useSites } from '../../sites/api';
import { scheduleErrorMessage, useArchiveSchedulePattern, useSchedulePatterns } from '../api';
import { shiftsCode, todayInZone, weekdayShort } from '../format';

import { ApplicationsSection } from './applications-section';
import { ApplyPatternDialog } from './apply-pattern-dialog';
import { PatternEditorDialog } from './pattern-editor-dialog';

interface PatternFilters {
  status: CatalogStatusFilter;
}

const DEFAULT_FILTERS: PatternFilters = { status: 'ACTIVE' };

/** "Semanal" / "Ciclo de 8 días". */
export function patternKindLabel(pattern: Pick<SchedulePattern, 'kind' | 'cycle_days'>): string {
  return pattern.kind === 'WEEKLY'
    ? t('schedules.patterns.kind.WEEKLY')
    : t('schedules.patterns.kind.CYCLE', { n: pattern.cycle_days });
}

/** Código de cada día del ciclo: "M M M M D D D D"; en un semanal, "lu M · ma M · …". */
export function patternCycleSummary(pattern: Pick<SchedulePattern, 'kind' | 'days'>): string {
  const sorted = [...pattern.days].sort((a, b) => a.day_index - b.day_index);
  const codes = sorted.map((day) =>
    day.kind === 'REST' ? t('schedules.grid.cell.restCode') : shiftsCode(day.shifts),
  );
  if (pattern.kind === 'WEEKLY') {
    return codes.map((code, index) => `${weekdayShort(index + 1)} ${code}`).join(' · ');
  }
  return codes.join(' ');
}

export function PatternsTab() {
  const me = useMe();
  const { readOnly } = useReadOnly();
  const { toast } = useToast();
  const canConfigure = hasPermission(me.data, 'schedules.configure');
  const canWrite = hasPermission(me.data, 'schedules.write');
  const today = todayInZone(me.data?.company?.default_timezone ?? 'America/Lima');

  const { draft, applied, update, apply, clear, dirty, isFiltered, canClear } =
    useAppliedFilters<PatternFilters>(DEFAULT_FILTERS);
  const query = useSchedulePatterns(applied.status);
  const patterns = query.data ?? [];
  const allPatterns = useSchedulePatterns('ALL');
  const sites = useSites({ status: 'ACTIVE' });
  const archive = useArchiveSchedulePattern();

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<SchedulePattern | undefined>(undefined);
  const [applying, setApplying] = useState<SchedulePattern | null>(null);
  const [archiving, setArchiving] = useState<SchedulePattern | null>(null);

  const statusOptions: SelectOption[] = useMemo(
    () =>
      CatalogStatusFilterSchema.options.map((status) => ({
        value: status,
        label: t(`schedules.patterns.filters.status.${status}`),
      })),
    [],
  );

  function confirmArchive(pattern: SchedulePattern) {
    archive.mutate(
      { id: pattern.id },
      {
        onSuccess: () => {
          toast({
            title: t('schedules.patterns.toast.archived', { name: pattern.name }),
            variant: 'success',
          });
          setArchiving(null);
        },
        onError: (error) => toast({ title: scheduleErrorMessage(error), variant: 'error' }),
      },
    );
  }

  return (
    <div className="flex flex-col gap-fm-6">
      <div className="flex flex-col gap-fm-4">
        <div className="flex flex-wrap items-center justify-between gap-fm-3">
          <p className="text-fm-sm text-fm-text-secondary">{t('schedules.patterns.description')}</p>
          <div className="flex flex-wrap items-center gap-fm-2">
            <RefreshButton query={query} />
            {canConfigure ? (
              <Button
                variant="compact"
                size="md"
                icon={<Plus size={20} />}
                data-testid="pattern-new"
                onClick={() => setCreating(true)}
                {...readOnlyProps(readOnly)}
              >
                {t('schedules.patterns.new')}
              </Button>
            ) : null}
          </div>
        </div>

        <Card>
          <FilterForm onApply={apply} className="block">
            <fieldset className="flex flex-wrap items-end gap-fm-3">
              <legend className="sr-only">{t('schedules.patterns.filters.legend')}</legend>
              <Field label={t('schedules.patterns.filters.statusLabel')} className="w-[200px]">
                {(control) => (
                  <Select
                    id={control.id}
                    aria-describedby={control['aria-describedby']}
                    value={draft.status}
                    onValueChange={(value) =>
                      update({ status: CatalogStatusFilterSchema.parse(value) })
                    }
                    options={statusOptions}
                  />
                )}
              </Field>
              <FilterActions onSearch={apply} dirty={dirty} canClear={canClear} onClear={clear} />
            </fieldset>
          </FilterForm>
        </Card>

        {query.isPending ? <SkeletonLines lines={4} /> : null}

        {query.isError ? (
          <Card>
            <CardTitle>{t('schedules.patterns.error.title')}</CardTitle>
            <CardDescription>{scheduleErrorMessage(query.error)}</CardDescription>
            <Button className="mt-fm-4" size="sm" onClick={() => void query.refetch()}>
              {t('common.buttons.retry')}
            </Button>
          </Card>
        ) : null}

        {query.isSuccess && patterns.length === 0 ? (
          <EmptyState
            icon={Repeat}
            title={
              isFiltered
                ? t('schedules.patterns.noResults.title')
                : t('schedules.patterns.empty.title')
            }
            description={
              isFiltered
                ? t('schedules.patterns.noResults.description')
                : t('schedules.patterns.empty.description')
            }
          />
        ) : null}

        {patterns.length > 0 ? (
          <Table data-testid="patterns-table">
            <TableCaption>{t('schedules.tabs.patrones')}</TableCaption>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{t('schedules.patterns.columns.name')}</TableHeaderCell>
                <TableHeaderCell>{t('schedules.patterns.columns.kind')}</TableHeaderCell>
                <TableHeaderCell>{t('schedules.patterns.columns.cycle')}</TableHeaderCell>
                <TableHeaderCell>{t('schedules.patterns.columns.source')}</TableHeaderCell>
                <TableHeaderCell>{t('schedules.patterns.columns.status')}</TableHeaderCell>
                <TableHeaderCell>
                  <span className="sr-only">{t('schedules.patterns.columns.actions')}</span>
                </TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {patterns.map((pattern) => {
                const integration = pattern.source === 'INTEGRATION';
                const active = pattern.status === 'ACTIVE';
                const editable = canConfigure && active && !integration;
                const summary = patternCycleSummary(pattern);
                return (
                  <TableRow key={pattern.id} data-testid="pattern-row" data-status={pattern.status}>
                    <TableCell className="font-semibold">{pattern.name}</TableCell>
                    <TableCell>{patternKindLabel(pattern)}</TableCell>
                    <TableCell className="max-w-[320px]">
                      <span className="block truncate fm-tabular" title={summary}>
                        {summary}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-fm-1">
                        {integration ? <Lock size={12} aria-hidden="true" /> : null}
                        {t(`schedules.source.${pattern.source}`)}
                      </span>
                    </TableCell>
                    <TableCell>
                      {active ? (
                        <Badge variant="activa">{t('schedules.patterns.status.ACTIVE')}</Badge>
                      ) : (
                        <Badge variant="inactiva">{t('schedules.patterns.status.ARCHIVED')}</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap justify-end gap-fm-1">
                        {canWrite && active ? (
                          <Button
                            variant="secondary"
                            size="sm"
                            data-testid="pattern-apply"
                            aria-label={t('schedules.patterns.actions.applyLabel', {
                              name: pattern.name,
                            })}
                            onClick={() => setApplying(pattern)}
                            {...readOnlyProps(readOnly)}
                          >
                            {t('schedules.patterns.actions.apply')}
                          </Button>
                        ) : null}
                        {editable ? (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid="pattern-edit"
                              aria-label={t('schedules.patterns.actions.editLabel', {
                                name: pattern.name,
                              })}
                              onClick={() => setEditing(pattern)}
                              {...readOnlyProps(readOnly)}
                            >
                              {t('schedules.patterns.actions.edit')}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid="pattern-archive"
                              aria-label={t('schedules.patterns.actions.archiveLabel', {
                                name: pattern.name,
                              })}
                              onClick={() => setArchiving(pattern)}
                              {...readOnlyProps(readOnly)}
                            >
                              {t('schedules.patterns.actions.archive')}
                            </Button>
                          </>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : null}
      </div>

      <ApplicationsSection patterns={allPatterns.data ?? []} canWrite={canWrite} today={today} />

      {canConfigure ? (
        <>
          <PatternEditorDialog
            open={creating || editing !== undefined}
            onOpenChange={(open) => {
              if (!open) {
                setCreating(false);
                setEditing(undefined);
              }
            }}
            pattern={editing}
            today={today}
          />
          <Dialog
            open={archiving !== null}
            onOpenChange={(open) => (open ? undefined : setArchiving(null))}
          >
            <DialogContent
              title={t('schedules.patterns.archive.title', { name: archiving?.name ?? '' })}
              description={t('schedules.patterns.archive.description')}
              footer={
                <>
                  <Button
                    variant="ghost"
                    onClick={() => setArchiving(null)}
                    disabled={archive.isPending}
                  >
                    {t('common.buttons.back')}
                  </Button>
                  <Button
                    variant="destructive"
                    data-testid="pattern-archive-confirm"
                    loading={archive.isPending}
                    loadingText={t('common.states.saving')}
                    onClick={() => (archiving ? confirmArchive(archiving) : undefined)}
                    {...readOnlyProps(readOnly)}
                  >
                    {t('schedules.patterns.archive.confirm')}
                  </Button>
                </>
              }
            >
              <p className="text-fm-sm text-fm-text-secondary">
                {t('schedules.patterns.archive.notice')}
              </p>
            </DialogContent>
          </Dialog>
        </>
      ) : null}

      {canWrite ? (
        <ApplyPatternDialog
          pattern={applying}
          onClose={() => setApplying(null)}
          sites={sites.data ?? []}
          today={today}
        />
      ) : null}
    </div>
  );
}
